import React, { useState, useEffect, useCallback } from "react";
import {
  Save,
  Loader2,
  Upload,
  Eye,
  FileText,
  ChevronDown,
  ArrowLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../api/api";
import MainLayout from "../sidebar/Sidebar.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function CreateProposal() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [location, setLocation] = useState("Proposals");

  // Config form state
  const [config, setConfig] = useState({
    company_name: "",
    company_address: "",
    company_phone: "",
    company_email: "",
    logo_s3_key: "",
    logo_position: "left",
    show_line_item_prices: true,
    show_quantities: true,
    show_total: true,
    show_client_name: true,
    show_client_email: true,
    show_client_phone: true,
    contract_terms: "",
    header_text: "",
    footer_text: "",
    accent_color: "#006de2",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Project selection for generating proposals
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [proposalTitle, setProposalTitle] = useState("");

  // Client/Lead selection
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [clientDisplayOptions, setClientDisplayOptions] = useState({
    show_client_name: true,
    show_client_email: true,
    show_client_phone: true,
  });

  // Fetch existing config
  const { data: existingConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["proposalConfig"],
    queryFn: async () => {
      try {
        const token = await import("../firebase").then(m => m.auth.currentUser?.getIdToken());
        const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const response = await fetch(`${baseUrl}/proposals/config`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 404) {
          return null;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch config");
        }

        return response.json();
      } catch (error) {
        if (error.message !== "Failed to fetch config") {
          console.error("Error fetching proposal config:", error);
        }
        return null;
      }
    },
    retry: false,
  });

  // Fetch projects for the dropdown
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const response = await api.get("/projects/");
      return response.data;
    },
  });

  // Fetch leads for client selection
  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => {
      const response = await api.get("/leads/");
      return response.data;
    },
  });

  // Update config state when existing config is loaded
  useEffect(() => {
    if (existingConfig) {
      setConfig({
        company_name: existingConfig.company_name || "",
        company_address: existingConfig.company_address || "",
        company_phone: existingConfig.company_phone || "",
        company_email: existingConfig.company_email || "",
        logo_s3_key: existingConfig.logo_s3_key || "",
        logo_position: existingConfig.logo_position || "left",
        show_line_item_prices: existingConfig.show_line_item_prices ?? true,
        show_quantities: existingConfig.show_quantities ?? true,
        show_total: existingConfig.show_total ?? true,
        show_client_name: existingConfig.show_client_name ?? true,
        show_client_email: existingConfig.show_client_email ?? true,
        show_client_phone: existingConfig.show_client_phone ?? true,
        contract_terms: existingConfig.contract_terms || "",
        header_text: existingConfig.header_text || "",
        footer_text: existingConfig.footer_text || "",
        accent_color: existingConfig.accent_color || "#006de2",
      });
    }
  }, [existingConfig]);

  // Save config mutation
  const saveConfigMutation = useMutation({
    mutationFn: async (configData) => {
      const response = await api.post("/proposals/config", configData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["proposalConfig"]);
      toast.success("Proposal settings saved successfully");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    },
  });

  // Generate proposal mutation
  const generateProposalMutation = useMutation({
    mutationFn: async ({ projectId, title, leadId, clientOptions }) => {
      const response = await api.post(`/proposals/generate/${projectId}`, {
        title,
        expires_days: 30,
        lead_id: leadId || null,
        show_client_name: clientOptions?.show_client_name ?? true,
        show_client_email: clientOptions?.show_client_email ?? true,
        show_client_phone: clientOptions?.show_client_phone ?? true,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["proposals"]);
      toast.success("Proposal generated successfully!");
      navigate("/proposals");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to generate proposal");
    },
  });

  // Handle logo file selection
  const handleLogoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Upload logo to S3
  const handleLogoUpload = useCallback(async () => {
    if (!logoFile) return;

    setIsUploadingLogo(true);
    try {
      const { data: uploadData } = await api.post("/proposals/logo-upload");

      const formData = new FormData();
      Object.entries(uploadData.post_fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("Content-Type", logoFile.type);
      formData.append("file", logoFile);

      await fetch(uploadData.post_url, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      setConfig((prev) => ({
        ...prev,
        logo_s3_key: uploadData.logo_key,
      }));

      toast.success("Logo uploaded successfully");
      setLogoFile(null);
    } catch (error) {
      toast.error("Failed to upload logo");
      console.error("Logo upload error:", error);
    } finally {
      setIsUploadingLogo(false);
    }
  }, [logoFile]);

  // Save configuration
  const handleSaveConfig = useCallback(() => {
    saveConfigMutation.mutate(config);
  }, [config, saveConfigMutation]);

  // Preview proposal
  const handlePreview = useCallback(async (projectId) => {
    try {
      const response = await api.get(`/proposals/preview/${projectId}`, {
        responseType: "text",
      });
      const previewWindow = window.open("", "_blank");
      if (previewWindow) {
        previewWindow.document.write(response.data);
        previewWindow.document.close();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load preview");
    }
  }, []);

  if (isLoadingConfig) {
    return (
      <MainLayout location={location} setLocation={setLocation}>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-1 overflow-hidden py-6 px-5 min-h-0">
          <div className="h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 pb-6 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/proposals")}
                className="mr-2"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold" style={{ color: "#1C1C1C" }}>
                  Create Proposal
                </h1>
                <p className="text-sm" style={{ color: "#727272" }}>
                  Configure settings and generate a new PDF proposal
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="max-w-3xl space-y-8">
                {/* Company Information */}
                <div
                  className="bg-white rounded-xl p-6"
                  style={{ border: "1px solid #e8e8e8" }}
                >
                  <h3 className="text-base font-semibold mb-4" style={{ color: "#1C1C1C" }}>
                    Company Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={config.company_name}
                        onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        placeholder="Your Company Name"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Company Address
                      </label>
                      <textarea
                        value={config.company_address}
                        onChange={(e) => setConfig({ ...config, company_address: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        rows={2}
                        placeholder="123 Main St, City, State 12345"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={config.company_phone}
                        onChange={(e) => setConfig({ ...config, company_phone: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={config.company_email}
                        onChange={(e) => setConfig({ ...config, company_email: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        placeholder="contact@company.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo Settings */}
                <div
                  className="bg-white rounded-xl p-6"
                  style={{ border: "1px solid #e8e8e8" }}
                >
                  <h3 className="text-base font-semibold mb-4" style={{ color: "#1C1C1C" }}>
                    Logo
                  </h3>
                  <div className="flex items-start gap-6">
                    <div className="flex-1">
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer"
                        onClick={() => document.getElementById("logo-upload").click()}
                      >
                        {logoPreview || config.logo_s3_key ? (
                          <img
                            src={logoPreview || ""}
                            alt="Logo preview"
                            className="max-h-20 mx-auto"
                          />
                        ) : (
                          <div className="py-4">
                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">Click to upload logo</p>
                            <p className="text-xs text-gray-400">PNG, JPG up to 2MB</p>
                          </div>
                        )}
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoChange}
                        />
                      </div>
                      {logoFile && (
                        <Button
                          className="mt-3 w-full"
                          onClick={handleLogoUpload}
                          disabled={isUploadingLogo}
                          variant="outline"
                        >
                          {isUploadingLogo ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload Logo
                        </Button>
                      )}
                    </div>
                    <div className="w-48">
                      <label className="block text-sm font-medium mb-2" style={{ color: "#727272" }}>
                        Logo Position
                      </label>
                      <div className="space-y-2">
                        {["left", "center", "right"].map((pos) => (
                          <label key={pos} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="logo_position"
                              value={pos}
                              checked={config.logo_position === pos}
                              onChange={(e) => setConfig({ ...config, logo_position: e.target.value })}
                              className="text-blue-600"
                            />
                            <span className="text-sm capitalize" style={{ color: "#424242" }}>
                              {pos}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Display Options */}
                <div
                  className="bg-white rounded-xl p-6"
                  style={{ border: "1px solid #e8e8e8" }}
                >
                  <h3 className="text-base font-semibold mb-4" style={{ color: "#1C1C1C" }}>
                    Display Options
                  </h3>
                  <div className="space-y-3">
                    {[
                      { key: "show_quantities", label: "Show Quantities" },
                      { key: "show_line_item_prices", label: "Show Line Item Prices" },
                      { key: "show_total", label: "Show Total Amount" },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config[key]}
                          onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm" style={{ color: "#424242" }}>
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: "#727272" }}>
                      Accent Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={config.accent_color}
                        onChange={(e) => setConfig({ ...config, accent_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.accent_color}
                        onChange={(e) => setConfig({ ...config, accent_color: e.target.value })}
                        className="px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Content Sections */}
                <div
                  className="bg-white rounded-xl p-6"
                  style={{ border: "1px solid #e8e8e8" }}
                >
                  <h3 className="text-base font-semibold mb-4" style={{ color: "#1C1C1C" }}>
                    Content
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Header Text (appears below project info)
                      </label>
                      <textarea
                        value={config.header_text}
                        onChange={(e) => setConfig({ ...config, header_text: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        rows={3}
                        placeholder="Thank you for the opportunity to provide this proposal..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Terms & Conditions
                      </label>
                      <textarea
                        value={config.contract_terms}
                        onChange={(e) => setConfig({ ...config, contract_terms: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        rows={6}
                        placeholder="1. Payment terms: 50% deposit, 50% upon completion&#10;2. Work to begin within 14 days of signed agreement&#10;3. Price valid for 30 days from proposal date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "#727272" }}>
                        Footer Text
                      </label>
                      <input
                        type="text"
                        value={config.footer_text}
                        onChange={(e) => setConfig({ ...config, footer_text: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ border: "1px solid #e8e8e8", color: "#424242" }}
                        placeholder="Thank you for your business!"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Settings Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveConfig}
                    disabled={saveConfigMutation.isPending}
                    variant="outline"
                    className="mr-3"
                  >
                    {saveConfigMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Settings
                  </Button>
                </div>

                {/* Generate Proposal Section */}
                <div
                  className="bg-white rounded-xl p-6"
                  style={{ border: "1px solid #e8e8e8" }}
                >
                  <h3 className="text-base font-semibold mb-4" style={{ color: "#1C1C1C" }}>
                    Generate Proposal
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#727272" }}>
                        Proposal Title
                      </label>
                      <input
                        type="text"
                        value={proposalTitle}
                        onChange={(e) => setProposalTitle(e.target.value)}
                        className="w-full px-3 py-2 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ color: "#424242", border: "1px solid #e8e8e8" }}
                        placeholder="Enter a unique name for this proposal..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#727272" }}>
                        Select Project
                      </label>
                      <div className="relative">
                        <select
                          value={selectedProjectId}
                          onChange={(e) => setSelectedProjectId(e.target.value)}
                          className="w-full appearance-none px-3 py-2 pr-10 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ color: "#424242", border: "1px solid #e8e8e8" }}
                        >
                          <option value="">Select a project...</option>
                          {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name} ({project.project_code})
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: "#727272" }}>
                        Select Client (Optional)
                      </label>
                      <div className="relative">
                        <select
                          value={selectedLeadId}
                          onChange={(e) => setSelectedLeadId(e.target.value)}
                          className="w-full appearance-none px-3 py-2 pr-10 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ color: "#424242", border: "1px solid #e8e8e8" }}
                        >
                          <option value="">No client selected</option>
                          {leads.map((lead) => (
                            <option key={lead.id} value={lead.id}>
                              {lead.name} {lead.email ? `(${lead.email})` : ""}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
                      </div>
                    </div>

                    {selectedLeadId && (
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: "#727272" }}>
                          Client Display Options
                        </label>
                        <div className="space-y-2">
                          {[
                            { key: "show_client_name", label: "Show Client Name" },
                            { key: "show_client_email", label: "Show Client Email" },
                            { key: "show_client_phone", label: "Show Client Phone" },
                          ].map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientDisplayOptions[key]}
                                onChange={(e) =>
                                  setClientDisplayOptions({
                                    ...clientDisplayOptions,
                                    [key]: e.target.checked,
                                  })
                                }
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <span className="text-sm" style={{ color: "#424242" }}>
                                {label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      {selectedProjectId && (
                        <Button
                          variant="outline"
                          onClick={() => handlePreview(selectedProjectId)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </Button>
                      )}
                      <Button
                        onClick={() =>
                          generateProposalMutation.mutate({
                            projectId: selectedProjectId,
                            title: proposalTitle,
                            leadId: selectedLeadId || null,
                            clientOptions: clientDisplayOptions,
                          })
                        }
                        disabled={!selectedProjectId || !proposalTitle.trim() || generateProposalMutation.isPending}
                        style={{ backgroundColor: "#006de2", color: "white" }}
                      >
                        {generateProposalMutation.isPending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4 mr-2" />
                        )}
                        Generate PDF
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Bottom spacing */}
                <div className="h-6" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
